import {
  APPROVAL_STATUS,
  APPROVAL_STATUS_UI,
  DEPOSIT_BALANCE_STATUS,
  PAYMENT_BREAKDOWN_STATUS_UI,
  PAYMENT_METHODS
} from './detailsConstants'
import { STATUS_CANCELLED } from '../../constants/chargeConstants'

export const safeArray = (value) => (Array.isArray(value) ? value : [])

export const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

export const parseCustomFieldsItem = (value) => {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string' || !value.trim() || value.trim() === '[]') return []

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    console.error('Error al parsear custom_fields_item:', error, value)
    return []
  }
}

export const toNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const getNestedValue = (source, path) => {
  if (!source || !path) return undefined
  return path.split('.').reduce((current, key) => current?.[key], source)
}

export const getFirstValue = (source, paths, fallback = '') => {
  for (const path of paths) {
    const value = getNestedValue(source, path)
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return fallback
}

export const getFirstNumber = (source, paths, fallback = 0) => {
  for (const path of paths) {
    const value = getNestedValue(source, path)
    if (value !== undefined && value !== null && value !== '') {
      return toNumber(value, fallback)
    }
  }
  return fallback
}

export const buildPaymentMethodCards = (settlement) =>
  PAYMENT_METHODS.map((method) => ({
    ...method,
    amount: getFirstNumber(settlement, method.amountPaths)
  }))

export const getDeliveryCreditAmount = (settlement) =>
  getFirstNumber(settlement, [
    'payment_method_totals.credits',
    'payment_method_totals.delivery_credit',
    'delivery_credit_amount'
  ])

export const isPolicePaymentEnabled = (settlement) => Boolean(settlement?.is_police_enabled)

export const getPolicePaymentAmount = (settlement) => {
  const amount = getFirstNumber(settlement, ['payment_amount'])
  return amount ? -Math.abs(amount) : 0
}

export const getTotalCollectedAmount = (settlement) =>
  getFirstNumber(
    settlement,
    ['total_amount'],
    buildPaymentMethodCards(settlement).reduce((sum, method) => sum + method.amount, 0)
  )

export const getTotalToDeposit = (settlement) =>
  getFirstNumber(
    settlement,
    ['total_to_deposit', 'total_to_deposit_amount'],
    getFirstNumber(settlement, ['cash_amount']) +
      getFirstNumber(settlement, ['cheque_amount']) -
      (isPolicePaymentEnabled(settlement)
        ? Math.abs(getFirstNumber(settlement, ['payment_amount']))
        : 0)
  )

export const getTotalDeposited = (settlement) => {
  return getFirstNumber(settlement, ['total_deposited', 'total_deposited_amount'])
}

export const isDepositBalanced = (settlement) => {
  if (!settlement) return false

  const depositRows = buildReceiptRows(settlement).filter((row) => row.type === 'Depósito')
  const totalDeposited = depositRows.reduce((sum, row) => sum + toNumber(row.amount), 0)

  return Math.round(getTotalToDeposit(settlement) * 100) === Math.round(totalDeposited * 100)
}

export const resolveApprovalStatus = (settlement) =>
  settlement?.approval_status || settlement?.status

export const getApprovalStatusUI = (settlement) => {
  const status = resolveApprovalStatus(settlement)
  return (
    APPROVAL_STATUS_UI[status] || {
      label: '',
      className: '',
      color: '',
      backgroundColor: ''
    }
  )
}

export const getPaymentStatusUI = (status) => {
  const normalizedStatus = normalizeText(status)

  if (!normalizedStatus) return null

  return normalizedStatus === STATUS_CANCELLED
    ? PAYMENT_BREAKDOWN_STATUS_UI.CANCELLED
    : PAYMENT_BREAKDOWN_STATUS_UI.ACTIVE
}

export const isDocumentCredit = (model) =>
  !model?.charge_uid && model?.child_document_type == 'Factura'

export const sortNewestFirst = (rows, dateKey = 'date_of_charge') =>
  [...safeArray(rows)].sort((a, b) => {
    const left = new Date(a?.[dateKey] || 0).getTime()
    const right = new Date(b?.[dateKey] || 0).getTime()
    return right - left
  })

const DELIVERED_PRODUCT_COLLECTION_KEYS = [
  'delivered_products',
  'products_delivered',
  'delivered_product_details',
  'settlement_delivered_products',
  'collection_delivered_products',
  'delivery_products',
  'product_deliveries',
  'received_products',
  'products'
]

const DELIVERED_PRODUCT_CONTAINER_KEYS = [
  'delivery',
  'delivery_receipts',
  'deliveries',
  'delivery_documents',
  'delivery_orders',
  'orders',
  'collections',
  'charges',
  'documents',
  'receipts',
  'details'
]

const DELIVERED_PRODUCT_DETAIL_KEYS = [
  ...DELIVERED_PRODUCT_COLLECTION_KEYS,
  'delivery_receipt_details',
  'delivery_details',
  'order_details',
  'invoice_details',
  'document_details',
  'charge_details',
  'receipt_details',
  'items',
  'details'
]

const DELIVERED_PRODUCT_CODE_KEYS = [
  'product_db_ref',
  'product_uid',
  'product_code',
  'product_ref',
  'product_sku',
  'db_ref',
  'code'
]

const DELIVERED_PRODUCT_BARCODE_KEYS = [
  'product_barcode',
  'barcode',
  'bar_code',
  'product_ean',
  'ean',
  'upc'
]

const DELIVERED_PRODUCT_NAME_KEYS = [
  'product_description',
  'product_name',
  'name',
  'description',
  'item_description'
]

const DELIVERED_PRODUCT_UNIT_KEYS = [
  'unit_of_sale',
  'unit_of_measure',
  'unit_of_measurement',
  'sale_unit',
  'unit',
  'unit_name'
]

const DELIVERED_PRODUCT_QUANTITY_KEYS = [
  'quantity',
  'delivered_quantity',
  'quantity_delivered',
  'product_quantity',
  'total_quantity',
  'qty'
]

const hasDeliveredProductIdentity = (row) =>
  Boolean(
    getFirstValue(row, DELIVERED_PRODUCT_CODE_KEYS) ||
    getFirstValue(row, DELIVERED_PRODUCT_NAME_KEYS)
  )

const appendDeliveredProductRows = (rows, source, includeSource = true) => {
  if (!source) return

  if (includeSource && hasDeliveredProductIdentity(source)) {
    rows.push(source)
  }

  DELIVERED_PRODUCT_DETAIL_KEYS.forEach((key) => {
    safeArray(source?.[key]).forEach((detail) => appendDeliveredProductRows(rows, detail))
  })
}

export const normalizeDeliveredProductRow = (row, index = 0) => ({
  id:
    row?.id ||
    row?.uuid ||
    row?.product_uuid ||
    `${getFirstValue(row, DELIVERED_PRODUCT_CODE_KEYS, 'product')}-${index}`,
  productCode: String(getFirstValue(row, DELIVERED_PRODUCT_CODE_KEYS, '-')),
  barcode: String(getFirstValue(row, DELIVERED_PRODUCT_BARCODE_KEYS, '-')),
  productName: String(getFirstValue(row, DELIVERED_PRODUCT_NAME_KEYS, '-')),
  unitOfSale: String(getFirstValue(row, DELIVERED_PRODUCT_UNIT_KEYS, '-')),
  quantity: getFirstNumber(row, DELIVERED_PRODUCT_QUANTITY_KEYS, 0),
  raw: row
})

export const buildDeliveredProductsRows = (settlement) => {
  const directRows = []

  DELIVERED_PRODUCT_COLLECTION_KEYS.forEach((key) => {
    safeArray(settlement?.[key]).forEach((row) => appendDeliveredProductRows(directRows, row))
  })

  const sourceRows =
    directRows.length > 0
      ? directRows
      : DELIVERED_PRODUCT_CONTAINER_KEYS.reduce((rows, key) => {
          safeArray(settlement?.[key]).forEach((container) =>
            appendDeliveredProductRows(rows, container, false)
          )
          return rows
        }, [])

  const groupedRows = new Map()

  sourceRows
    .map((row, index) => normalizeDeliveredProductRow(row, index))
    .filter((row) => row.productCode !== '-' || row.productName !== '-')
    .forEach((row) => {
      const groupKey = [row.productCode, row.barcode, row.productName, row.unitOfSale]
        .map((value) => String(value).trim().toLowerCase())
        .join('|')
      const currentRow = groupedRows.get(groupKey)

      if (currentRow) {
        groupedRows.set(groupKey, {
          ...currentRow,
          quantity: currentRow.quantity + row.quantity
        })
        return
      }

      groupedRows.set(groupKey, row)
    })

  return Array.from(groupedRows.values()).sort((left, right) =>
    left.productCode.localeCompare(right.productCode, undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  )
}

export const getEvidenceImages = (record) => {
  const customFields = parseCustomFieldsItem(record?.custom_fields_item)
  const customFieldImages = customFields
    .filter(
      (field) =>
        (field?.code === 'charge_payment_image' || field?.fieldType === 'IMAGE_FIELD') &&
        field?.value
    )
    .flatMap((field) =>
      String(field.value)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => ({
          src: value,
          downloadUrl: value,
          title: field.label || record?.reference || record?.number || record?.uid || 'Comprobante',
          filename: field.filename || record?.filename || 'comprobante'
        }))
    )

  const candidates = [
    ...safeArray(record?.images),
    ...safeArray(record?.image_urls),
    ...safeArray(record?.attachments),
    record?.image_url,
    record?.receipt_url,
    record?.file_url,
    record?.download_url,
    record?.url,
    record?.image?.url
  ].filter(Boolean)

  const normalizedCandidates = candidates
    .map((candidate, index) => {
      if (typeof candidate === 'string') {
        return {
          src: candidate,
          downloadUrl: candidate,
          title: record?.reference || record?.number || record?.uid || `Comprobante ${index + 1}`,
          filename: record?.filename || `comprobante-${index + 1}`
        }
      }

      const src =
        candidate.url ||
        candidate.image_url ||
        candidate.receipt_url ||
        candidate.file_url ||
        candidate.download_url

      return {
        src,
        downloadUrl: candidate.download_url || candidate.url || src,
        title:
          candidate.title || candidate.filename || record?.reference || `Comprobante ${index + 1}`,
        filename: candidate.filename || record?.filename || `comprobante-${index + 1}`
      }
    })
    .filter((image) => image.src)

  const uniqueImages = new Map()

  ;[...customFieldImages, ...normalizedCandidates].forEach((image, index) => {
    if (!image?.src) return
    if (uniqueImages.has(image.src)) return

    uniqueImages.set(image.src, {
      ...image,
      filename: image.filename || `comprobante-${index + 1}`
    })
  })

  return Array.from(uniqueImages.values())
}

export const getEvidenceFiles = (record) => {
  const customFields = parseCustomFieldsItem(record?.custom_fields_item)

  const files = customFields
    .filter(
      (field) =>
        (field?.code === 'charge_payment_file' || field?.fieldType === 'FILE_FIELD') && field?.value
    )
    .flatMap((field) =>
      String(field.value)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => ({
          url: value,
          title: field.label || null,
          filename: field.filename || null
        }))
    )
    .filter(
      (file, index, files) => files.findIndex((candidate) => candidate.url === file.url) === index
    )

  const titleTotals = files.reduce((totals, file) => {
    if (file.title) totals.set(file.title, (totals.get(file.title) || 0) + 1)
    return totals
  }, new Map())
  const filenameTotals = files.reduce((totals, file) => {
    if (file.filename) totals.set(file.filename, (totals.get(file.filename) || 0) + 1)
    return totals
  }, new Map())
  const titleOccurrences = new Map()
  const filenameOccurrences = new Map()

  return files.map((file, index) => {
    const filenameOccurrence = (filenameOccurrences.get(file.filename) || 0) + 1
    if (file.filename) filenameOccurrences.set(file.filename, filenameOccurrence)
    const extensionIndex = file.filename?.lastIndexOf('.') ?? -1
    const uniqueFilename =
      file.filename && filenameTotals.get(file.filename) > 1
        ? extensionIndex > 0
          ? `${file.filename.slice(0, extensionIndex)}-${filenameOccurrence}${file.filename.slice(extensionIndex)}`
          : `${file.filename}-${filenameOccurrence}`
        : file.filename

    if (!file.title) {
      return {
        ...file,
        title: `Adjunto ${index + 1}`,
        filename: uniqueFilename || `adjunto-${index + 1}`
      }
    }

    const occurrence = (titleOccurrences.get(file.title) || 0) + 1
    titleOccurrences.set(file.title, occurrence)
    const hasRepeatedTitle = titleTotals.get(file.title) > 1

    return {
      ...file,
      title: hasRepeatedTitle ? `${file.title} ${occurrence}` : file.title,
      filename: uniqueFilename || `adjunto-${index + 1}`
    }
  })
}

export const normalizePaymentBreakdownRow = (row, index, fallback = {}) => ({
  id:
    row.id ||
    row.uuid ||
    fallback.id ||
    `${fallback.chargeUid || row.charge_uid || 'payment'}-${index}`,
  date: row.date || row.datetime || row.paid_at || row.created_at || fallback.date,
  chargeUid: row.charge_uid || row.chargeUid || row.collection_uid || fallback.chargeUid,
  paymentType: row.payment_type || row.paymentType || row.type || fallback.paymentType,
  amount: toNumber(row.amount ?? row.payment_amount, fallback.amount ?? 0),
  bank: row.bank || row.bank_name || row.bank_short_name || fallback.bank || null,
  account: row.account || row.destination || row.account_destination || fallback.account || null,
  number:
    row.number ||
    row.document_number ||
    row.deposit_number ||
    row.cheque_number ||
    fallback.number ||
    null,
  reference: row.reference || row.transaction_reference || fallback.reference || null,
  status: row.charge_status || null,
  images: getEvidenceImages(row),
  files: getEvidenceFiles(row)
})

export const shouldIgnorePaymentBreakdownRow = (row) => {
  const paymentType = String(row?.payment_type || row?.paymentType || row?.type || '')
    .trim()
    .toLowerCase()
  const amount = toNumber(row?.amount ?? row?.payment_amount, 0)

  return paymentType === 'efectivo' && amount === 0
}

export const buildPaymentBreakdownRows = (settlement) => {
  const explicitRows = safeArray(settlement?.payment_breakdown)

  if (explicitRows.length > 0) {
    return explicitRows
      .filter((row) => !shouldIgnorePaymentBreakdownRow(row))
      .map((row, index) => normalizePaymentBreakdownRow(row, index))
  }

  const uniqueRows = new Map()

  sortNewestFirst(settlement?.details).forEach((detail, detailIndex) => {
    const payments = safeArray(detail?.payments)

    if (payments.length === 0) {
      return
    }

    payments.forEach((payment, paymentIndex) => {
      if (shouldIgnorePaymentBreakdownRow(payment)) return

      const mappedPayment = {
        id: payment?.id,
        header_uuid: payment?.header_uuid,
        payment_type: payment?.payment_type ?? null,
        amount: payment?.amount ?? null,
        reference: payment?.reference ?? null,
        bank_name: payment?.bank_name ?? null,
        custom_fields_item: payment?.custom_fields_item,
        images: payment?.images,
        image_urls: payment?.image_urls,
        attachments: payment?.attachments,
        image_url: payment?.image_url,
        receipt_url: payment?.receipt_url,
        file_url: payment?.file_url,
        download_url: payment?.download_url,
        url: payment?.url,
        image: payment?.image,
        charge_status: payment?.charge_status ?? null,
        date: payment?.date ?? null,
        account: null,
        number: null
      }

      const normalizedRow = normalizePaymentBreakdownRow(mappedPayment, paymentIndex, {
        id: `${detail.charge_uid || detail.child_document_reference || 'payment'}-${detailIndex}-${paymentIndex}`,
        chargeUid: detail.charge_uid,
        date: null,
        reference: null,
        number: null,
        paymentType: null,
        amount: 0
      })
      const dedupeKey = [
        payment.id || '',
        payment.header_uuid || '',
        normalizedRow.chargeUid || '',
        normalizedRow.paymentType || '',
        normalizedRow.amount
      ].join('|')

      if (!uniqueRows.has(dedupeKey)) {
        uniqueRows.set(dedupeKey, normalizedRow)
      }
    })
  })

  return Array.from(uniqueRows.values()).sort((left, right) => {
    const leftDate = new Date(left?.date || 0).getTime()
    const rightDate = new Date(right?.date || 0).getTime()
    return rightDate - leftDate
  })
}

export const buildReceiptRows = (settlement) => {
  const depositRows = [
    ...safeArray(settlement?.settlement_deposits),
    ...safeArray(settlement?.deposit_receipts),
    ...safeArray(settlement?.deposits),
    ...safeArray(settlement?.deposit_slips)
  ]

  const chequeRows = [
    ...safeArray(settlement?.cheque_receipts),
    ...safeArray(settlement?.cheques),
    ...safeArray(settlement?.cheque_slips)
  ]

  return [
    ...depositRows.map((row, index) => normalizeReceiptRow(row, 'Depósito', index)),
    ...chequeRows.map((row, index) => normalizeReceiptRow(row, 'Cheque', index))
  ]
}

export const mergeSavedDepositsIntoList = (existingDeposits, savedDeposits) => {
  if (!Array.isArray(existingDeposits) || existingDeposits.length === 0) {
    return existingDeposits
  }

  return existingDeposits.map((existing) => {
    const saved = safeArray(savedDeposits).find(
      (deposit) =>
        (deposit?.id != null &&
          existing?.id != null &&
          String(deposit.id) === String(existing.id)) ||
        (deposit?.uid && existing?.uid && deposit.uid === existing.uid)
    )

    if (!saved) return existing

    return {
      ...existing,
      amount: saved.amount,
      number: saved.number ?? existing.number,
      bank: saved.bank ?? existing.bank,
      bank_name: saved.bank ?? existing.bank_name,
      bank_uid: saved.bank_uid ?? existing.bank_uid,
      short_name: saved.short_name ?? existing.short_name,
      date: saved.date || existing.date,
      deposited_at: saved.date || existing.deposited_at
    }
  })
}

export const mergeSavedDepositsIntoSettlement = (settlement, savedDeposits) => {
  if (!settlement) return settlement

  const totalDeposited = safeArray(savedDeposits).reduce(
    (sum, deposit) => sum + toNumber(deposit?.amount),
    0
  )
  const balanced = getTotalToDeposit(settlement) === totalDeposited

  return {
    ...settlement,
    settlement_deposits: mergeSavedDepositsIntoList(
      settlement.settlement_deposits,
      savedDeposits
    ),
    deposit_receipts: mergeSavedDepositsIntoList(settlement.deposit_receipts, savedDeposits),
    deposits: mergeSavedDepositsIntoList(settlement.deposits, savedDeposits),
    deposit_slips: mergeSavedDepositsIntoList(settlement.deposit_slips, savedDeposits),
    total_deposited: totalDeposited,
    total_deposited_amount: totalDeposited,
    deposit_balance_status: balanced
      ? DEPOSIT_BALANCE_STATUS.BALANCED
      : DEPOSIT_BALANCE_STATUS.UNBALANCED
  }
}

const DEPOSIT_LIST_KEYS = [
  'settlement_deposits',
  'deposit_receipts',
  'deposits',
  'deposit_slips'
]

const isSameDeposit = (left, right) =>
  (left?.id != null && right?.id != null && String(left.id) === String(right.id)) ||
  (left?.uid && right?.uid && left.uid === right.uid)

const updateDepositList = (deposits, method, depositId, deposit) => {
  if (!Array.isArray(deposits)) return deposits

  if (method === 'DELETE') {
    return deposits.filter((item) => String(item?.id) !== String(depositId))
  }

  const existingIndex = deposits.findIndex((item) => isSameDeposit(item, deposit))

  if (existingIndex >= 0) {
    return deposits.map((item, index) => (index === existingIndex ? { ...item, ...deposit } : item))
  }

  return method === 'POST' ? [...deposits, deposit] : deposits
}

export const applyDepositMutationToSettlement = (settlement, method, depositId, deposit) => {
  if (!settlement) return settlement

  const nextSettlement = DEPOSIT_LIST_KEYS.reduce(
    (current, key) => ({
      ...current,
      [key]: updateDepositList(current[key], method, depositId, deposit)
    }),
    settlement
  )
  const deposits = safeArray(nextSettlement.settlement_deposits)
  const totalDeposited = deposits.reduce((sum, item) => sum + toNumber(item?.amount), 0)
  const balanced = Math.round(getTotalToDeposit(nextSettlement) * 100) === Math.round(totalDeposited * 100)

  return {
    ...nextSettlement,
    total_deposited: totalDeposited,
    total_deposited_amount: totalDeposited,
    deposit_balance_status: balanced
      ? DEPOSIT_BALANCE_STATUS.BALANCED
      : DEPOSIT_BALANCE_STATUS.UNBALANCED
  }
}

export const normalizeReceiptRow = (row, type, index) => ({
  id: row.id || row.uuid || `${type}-${index}`,
  type,
  uid: row.uid || row.deposit_uid || row.cheque_uid || row.number || `-`,
  bank: row.bank || row.bank_name || row.short_name || '-',
  bank_uid: row.bank_uid || null,
  short_name: row.short_name || null,
  number: row.number || row.deposit_number || row.cheque_number || row.reference || '-',
  date:
    row.date ||
    row.deposit_date ||
    row.deposited_at ||
    row.date_created_in_app ||
    row.created_at ||
    row.paid_at,
  amount: toNumber(row.amount ?? row.total ?? row.payment_amount),
  edit_history: row.edit_history || [],
  images: getEvidenceImages(row),
  raw: row
})
